// Signup spam prevention and validation utilities

// Rate limiting for signup attempts
export const checkSignupRateLimit = () => {
  const key = 'signup_attempts';
  const now = Date.now();
  const windowMs = 24 * 60 * 60 * 1000; // 24 hours
  
  // Allow more signups in development/localhost
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                       window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('localhost');
  
  const maxAttempts = isDevelopment ? 50 : 3; // 50 in dev, 3 in production
  
  try {
    const stored = localStorage.getItem(key);
    const attempts = stored ? JSON.parse(stored) : [];
    
    // Filter out old attempts
    const recentAttempts = attempts.filter(time => now - time < windowMs);
    
    if (recentAttempts.length >= maxAttempts) {
      const oldestAttempt = Math.min(...recentAttempts);
      const hoursLeft = Math.ceil((windowMs - (now - oldestAttempt)) / (60 * 60 * 1000));
      return { 
        allowed: false, 
        hoursLeft,
        message: `Too many signup attempts. Please wait ${hoursLeft} hours before trying again.`
      };
    }
    
    // Add current attempt
    recentAttempts.push(now);
    localStorage.setItem(key, JSON.stringify(recentAttempts));
    
    return { allowed: true };
  } catch (error) {
    console.warn('Rate limiting error:', error);
    return { allowed: true }; // Allow on error
  }
};

// Validate email for spam patterns
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return { isValid: false, reason: 'Email is required' };
  }

  const trimmedEmail = email.trim().toLowerCase();

  // Basic email format check
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(trimmedEmail)) {
    return { isValid: false, reason: 'Please enter a valid email address' };
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /temp|temporary|disposable|trash|fake|test/i, // Temporary email indicators
    /\d{10,}/, // Long number sequences
    /[a-z]{20,}/, // Extremely long strings
    /(.)\1{4,}/, // Repeated characters
  ];

  if (suspiciousPatterns.some(pattern => pattern.test(trimmedEmail))) {
    return { isValid: false, reason: 'Please use a valid business or personal email address' };
  }

  // Check for common spam email domains (you can expand this list)
  const spamDomains = [
    '10minutemail.com', 'guerrillamail.com', 'mailinator.com',
    'yopmail.com', 'temp-mail.org', 'throwaway.email',
    'getnada.com', 'maildrop.cc', 'mohmal.com'
  ];

  const domain = trimmedEmail.split('@')[1];
  if (spamDomains.includes(domain)) {
    return { isValid: false, reason: 'Please use a valid business or personal email address' };
  }

  return { isValid: true };
};

// Validate name fields for spam/inappropriate content
export const validateName = (name, fieldName) => {
  if (!name || typeof name !== 'string') {
    return { isValid: false, reason: `${fieldName} is required` };
  }

  const trimmedName = name.trim();

  // Length checks
  if (trimmedName.length === 0) {
    return { isValid: false, reason: `${fieldName} cannot be empty` };
  }

  if (trimmedName.length > 50) {
    return { isValid: false, reason: `${fieldName} must be 50 characters or less` };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, reason: `${fieldName} must be at least 2 characters` };
  }

  // Only allow letters, spaces, hyphens, and apostrophes for names
  if (!/^[a-zA-Z\s\-']+$/.test(trimmedName)) {
    return { isValid: false, reason: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }

  // Check for spam patterns
  const spamPatterns = [
    /(.)\1{3,}/, // Repeated characters
    /^[A-Z]{5,}$/, // All caps
    /test|admin|spam|fake/i, // Common spam words
  ];

  if (spamPatterns.some(pattern => pattern.test(trimmedName))) {
    return { isValid: false, reason: `Please enter your real ${fieldName.toLowerCase()}` };
  }

  return { isValid: true };
};

// Validate company name (optional field)
export const validateCompanyName = (companyName) => {
  if (!companyName || typeof companyName !== 'string' || companyName.trim() === '') {
    return { isValid: true }; // Optional field
  }

  const trimmedName = companyName.trim();

  // Length checks
  if (trimmedName.length > 100) {
    return { isValid: false, reason: 'Company name must be 100 characters or less' };
  }

  if (trimmedName.length < 2) {
    return { isValid: false, reason: 'Company name must be at least 2 characters' };
  }

  // More lenient character set for company names
  if (!/^[a-zA-Z0-9\s\-'&.,()]+$/.test(trimmedName)) {
    return { isValid: false, reason: 'Company name contains invalid characters' };
  }

  // Check for obvious spam
  const spamPatterns = [
    /(.)\1{4,}/, // Repeated characters
    /test|spam|fake|xxx/i, // Obvious spam words
    /(https?:\/\/|www\.)/i, // URLs
  ];

  if (spamPatterns.some(pattern => pattern.test(trimmedName))) {
    return { isValid: false, reason: 'Please enter a valid company name' };
  }

  return { isValid: true };
};

// Honeypot field validation (invisible field that bots fill out)
export const validateHoneypot = (honeypotValue) => {
  // If honeypot has any value, it's likely a bot
  return honeypotValue === '' || honeypotValue === undefined || honeypotValue === null;
};

// Time-based validation (check if form was filled too quickly)
export const validateFormTiming = (startTime) => {
  const now = Date.now();
  const timeTaken = now - startTime;
  const minTime = 5000; // 5 seconds minimum to fill form
  
  if (timeTaken < minTime) {
    return { 
      isValid: false, 
      reason: 'Please take a moment to review your information before submitting' 
    };
  }
  
  return { isValid: true };
};

// Master validation function for signup
export const validateSignupForm = (formData, startTime) => {
  const errors = {};

  // Rate limiting check
  const rateLimitCheck = checkSignupRateLimit();
  if (!rateLimitCheck.allowed) {
    return { isValid: false, errors: { general: rateLimitCheck.message } };
  }

  // Validate email
  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.reason;
  }

  // Validate first name
  const firstNameValidation = validateName(formData.firstName, 'First name');
  if (!firstNameValidation.isValid) {
    errors.firstName = firstNameValidation.reason;
  }

  // Validate last name
  const lastNameValidation = validateName(formData.lastName, 'Last name');
  if (!lastNameValidation.isValid) {
    errors.lastName = lastNameValidation.reason;
  }

  // Validate company name (optional)
  const companyValidation = validateCompanyName(formData.companyName);
  if (!companyValidation.isValid) {
    errors.companyName = companyValidation.reason;
  }

  // Validate honeypot
  if (!validateHoneypot(formData.honeypot)) {
    return { isValid: false, errors: { general: 'Spam detected. Please try again.' } };
  }

  // Validate timing
  if (startTime) {
    const timingValidation = validateFormTiming(startTime);
    if (!timingValidation.isValid) {
      errors.general = timingValidation.reason;
    }
  }

  // Password validation
  if (!formData.password || formData.password.length < 8) {
    errors.password = 'Password must be at least 8 characters long';
  }

  if (formData.password !== formData.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
}; 